import { useState } from "react";

import { isAxiosError, restProvider } from "@/api/rest";
import { AuthLayout } from "@/components/layouts";
import { AuthCard } from "@/components/ui/AuthCard";
import { CopyableValue } from "@/components/ui/CopyableValue";
import { InlineError } from "@/components/ui/InlineError";
import { Notice } from "@/components/ui/Notice";
import { ButtonLoader } from "@/components/ui/buttons/ButtonLoader";
import { routePaths } from "@/config/routes";

const HEADLINE = {
  lead: "Get help from",
  highlight: "SkillPanel support",
  description:
    "A support code lets the support team act as you while they look into a problem, without your password.",
};

const signInAgain = () => {
  window.location.assign(
    `${routePaths.LOGIN}?redirect_url=${encodeURIComponent(window.location.href)}`,
  );
};

export const SupportCodeView = () => {
  const [supportCode, setSupportCode] = useState("");
  const [failed, setFailed] = useState(false);

  const { generateSupportCode } = restProvider();

  const getSupportCode = async () => {
    setFailed(false);
    try {
      const response = await generateSupportCode();
      setSupportCode(response.code);
    } catch (e) {
      // The session ended while the page was open; signing in again brings the user back here
      if (isAxiosError(e) && e.response?.status === 401) {
        signInAgain();
        return;
      }
      setFailed(true);
    }
  };

  return (
    <AuthLayout title="Generate a support code" headline={HEADLINE}>
      <AuthCard>
        <p className="text-balance text-center text-base">
          If SkillPanel support asks for a support code, generate one here and send it to them.
        </p>
        {/* Once a code is shown it leads; generating another becomes the secondary action */}
        <ButtonLoader
          className="w-full"
          variant={supportCode ? "outline" : "default"}
          onClick={getSupportCode}
        >
          {supportCode ? "Generate a new code" : "Generate support code"}
        </ButtonLoader>
        {supportCode && (
          <>
            <CopyableValue label="Your support code" value={supportCode} prominent />
            {/* Redeeming takes auth:superadmin or auth:support (OAuth2TokenAuthorizationFilter) */}
            <Notice tone="info">
              Only SkillPanel support can use this code to act as you. It stops working after 24
              hours.
            </Notice>
          </>
        )}
        {failed && (
          <InlineError onRetry={getSupportCode}>
            {/* An earlier code is not revoked by a failed attempt, so it is still the one to send */}
            {supportCode
              ? "Couldn't generate a new code. The code above still works."
              : "Couldn't generate a support code. Check your connection and try again."}
          </InlineError>
        )}
        {/* Always present, so the new code is announced; spaced out so it is read letter by letter */}
        <span role="status" className="sr-only">
          {supportCode && `Support code generated: ${supportCode.split("").join(" ")}`}
        </span>
      </AuthCard>
    </AuthLayout>
  );
};
